import { useState, useEffect, useMemo, Fragment, type ReactNode } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { API_ENDPOINTS } from '../../config/api';
import { STATUS_COLORS } from '../../config/colors';
import type { SelectedItem, TimeRange } from '../../tabs/FleetIntelligence';

// Fix Leaflet default marker icon issue
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

interface VehicleMovement {
  entityId: string;
  points: Array<{
    lat: number;
    lon: number;
    timestamp: string;
    speed?: number;
    heading?: number;
  }>;
  source: 'optifleet' | 'declared';
  timeRange: {
    from: string;
    to: string;
  };
}

interface OrderRoute {
  entityId: string;
  stops: Array<{
    stopType: string;
    sequence?: number;
    location: {
      raw?: string;
      name?: string;
      coordinates?: { lat: number; lon: number };
    };
    timing?: {
      requestedStart?: string;
      requestedEnd?: string;
      actual?: string;
    };
  }>;
  associatedVehicles: string[];
  source: 'qargo' | 'declared';
  timeRange?: {
    from?: string;
    to?: string;
  };
}

interface InteractiveMapProps {
  selectedItems: SelectedItem[];
  timeRange: TimeRange;
  currentTimestamp: string | null;
  isPlaying: boolean;
  playbackSpeed: number;
}

// Component to auto-fit bounds
function MapBoundsUpdater({ bounds }: { bounds: L.LatLngBounds | null }) {
  const map = useMap();
  
  useEffect(() => {
    if (!bounds || !bounds.isValid()) return;
    
    // Ensure map is fully initialized
    if (!map || !map.getPane('mapPane')) return;
    
    // Use a timeout to ensure DOM is ready
    const timer = setTimeout(() => {
      try {
        // Only fit bounds if the map container exists and has size
        const container = map.getContainer();
        if (container && container.offsetWidth > 0 && container.offsetHeight > 0) {
          map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15, animate: false });
        }
      } catch (error) {
        console.warn('Error fitting map bounds:', error);
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [bounds, map]);
  
  return null;
}

// Polyline component with proper hover handling using Leaflet events
function HoverablePolyline({ 
  positions, 
  pathOptions, 
  onHover, 
  onLeave,
  children 
}: {
  positions: [number, number][];
  pathOptions: L.PolylineOptions;
  onHover?: () => void;
  onLeave?: () => void;
  children?: ReactNode;
}) {
  return (
    <Polyline
      positions={positions}
      pathOptions={pathOptions}
      eventHandlers={{
        mouseover: () => onHover?.(),
        mouseout: () => onLeave?.(),
      }}
    >
      {children}
    </Polyline>
  );
}

// Marker component with proper hover handling using Leaflet events
function HoverableMarker({ 
  position, 
  icon, 
  onHover, 
  onLeave,
  children 
}: {
  position: [number, number];
  icon?: L.Icon | L.DivIcon;
  onHover?: () => void;
  onLeave?: () => void;
  children?: ReactNode;
}) {
  return (
    <Marker
      position={position}
      icon={icon}
      eventHandlers={{
        mouseover: () => onHover?.(),
        mouseout: () => onLeave?.(),
      }}
    >
      {children}
    </Marker>
  );
}

export default function InteractiveMap({
  selectedItems,
  timeRange,
  currentTimestamp,
  isPlaying: _isPlaying,
  playbackSpeed: _playbackSpeed,
}: InteractiveMapProps) {
  // Future: use _isPlaying and _playbackSpeed for animated playback
  void _isPlaying;
  void _playbackSpeed;
  
  const [vehicleMovements, setVehicleMovements] = useState<Map<string, VehicleMovement>>(new Map());
  const [orderRoutes, setOrderRoutes] = useState<Map<string, OrderRoute>>(new Map());
  const [loading, setLoading] = useState(false);
  const [hoveredOrderId, setHoveredOrderId] = useState<string | null>(null);
  const [hoveredVehicleId, setHoveredVehicleId] = useState<string | null>(null);
  const [cachedData, setCachedData] = useState<{
    vehicles: Map<string, VehicleMovement>;
    orders: Map<string, OrderRoute>;
    timeRange: TimeRange;
  } | null>(null);

  // Fetch vehicle and order data
  useEffect(() => {
    const fetchData = async () => {
      // Check which items are already cached
      const missingVehicles: typeof selectedItems = [];
      const missingOrders: typeof selectedItems = [];
      const cachedVehicles = new Map<string, VehicleMovement>();
      const cachedOrders = new Map<string, OrderRoute>();
      
      if (
        cachedData &&
        cachedData.timeRange.from === timeRange.from &&
        cachedData.timeRange.to === timeRange.to
      ) {
        // Separate cached and missing items
        for (const item of selectedItems) {
          if (item.type === 'vehicle') {
            if (cachedData.vehicles.has(item.id)) {
              cachedVehicles.set(item.id, cachedData.vehicles.get(item.id)!);
            } else {
              missingVehicles.push(item);
            }
          } else if (item.type === 'order') {
            if (cachedData.orders.has(item.id)) {
              cachedOrders.set(item.id, cachedData.orders.get(item.id)!);
            } else {
              missingOrders.push(item);
            }
          }
        }
        
        // If all items are cached, use cache only
        if (missingVehicles.length === 0 && missingOrders.length === 0) {
          setVehicleMovements(cachedVehicles);
          setOrderRoutes(cachedOrders);
          
          console.log('[InteractiveMap] Using fully cached data:', {
            vehiclesCount: cachedVehicles.size,
            ordersCount: cachedOrders.size,
          });
          return;
        }
        
        console.log('[InteractiveMap] Fetching missing items:', {
          missingVehicles: missingVehicles.length,
          missingOrders: missingOrders.length,
        });
      } else {
        // No cache or different time range, fetch everything
        missingVehicles.push(...selectedItems.filter(item => item.type === 'vehicle'));
        missingOrders.push(...selectedItems.filter(item => item.type === 'order'));
      }

      setLoading(true);
      
      try {
        // Fetch only missing items
        const vehiclePromises = missingVehicles.map(async (vehicle) => {
          const res = await fetch(
            API_ENDPOINTS.fleetIntelligence.vehicles.movements(vehicle.id, timeRange)
          );
          if (!res.ok) return null;
          const data = await res.json();
          return { id: vehicle.id, data };
        });
        
        // Fetch all order routes in parallel
        const orderPromises = missingOrders.map(async (order) => {
          const res = await fetch(
            API_ENDPOINTS.fleetIntelligence.orders.movements(order.id, timeRange)
          );
          if (!res.ok) return null;
          const data = await res.json();
          return { id: order.id, data };
        });
        
        const [vehicleResults, orderResults] = await Promise.all([
          Promise.all(vehiclePromises),
          Promise.all(orderPromises),
        ]);
        
        // Build maps - start with cached data
        const newVehicles = new Map<string, VehicleMovement>(cachedVehicles);
        const newOrders = new Map<string, OrderRoute>(cachedOrders);
        
        // Add newly fetched data
        for (const result of vehicleResults) {
          if (result && result.data) {
            newVehicles.set(result.id, result.data);
          }
        }
        
        for (const result of orderResults) {
          if (result && result.data) {
            newOrders.set(result.id, result.data);
          }
        }
        
        setVehicleMovements(newVehicles);
        setOrderRoutes(newOrders);
        
        console.log('[InteractiveMap] Data loaded:', {
          vehiclesCount: newVehicles.size,
          ordersCount: newOrders.size,
          vehicleIds: Array.from(newVehicles.keys()),
          orderIds: Array.from(newOrders.keys()),
          cached: { vehicles: cachedVehicles.size, orders: cachedOrders.size },
          fetched: { vehicles: vehicleResults.filter(r => r).length, orders: orderResults.filter(r => r).length },
        });
        
        // Update cache with ALL fetched data
        const allVehicles = new Map(cachedData?.vehicles || new Map());
        const allOrders = new Map(cachedData?.orders || new Map());
        
        newVehicles.forEach((v, k) => allVehicles.set(k, v));
        newOrders.forEach((o, k) => allOrders.set(k, o));
        
        setCachedData({
          vehicles: allVehicles,
          orders: allOrders,
          timeRange,
        });
      } catch (error) {
        console.error('Error fetching map data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (selectedItems.length > 0) {
      fetchData();
    } else {
      setVehicleMovements(new Map());
      setOrderRoutes(new Map());
    }
  }, [selectedItems, timeRange]);

  // Filter points by current timestamp
  const filteredVehicleMovements = useMemo(() => {
    if (!currentTimestamp) return vehicleMovements;
    
    const filtered = new Map<string, VehicleMovement>();
    const currentTime = new Date(currentTimestamp).getTime();
    
    vehicleMovements.forEach((movement, id) => {
      const filteredPoints = movement.points.filter(point => {
        const pointTime = new Date(point.timestamp).getTime();
        return pointTime <= currentTime;
      });
      
      if (filteredPoints.length > 0) {
        filtered.set(id, { ...movement, points: filteredPoints });
      }
    });
    
    return filtered;
  }, [vehicleMovements, currentTimestamp]);

  // Calculate map bounds
  const mapBounds = useMemo(() => {
    const bounds = L.latLngBounds([]);
    let hasPoints = false;
    
    // Add vehicle points
    filteredVehicleMovements.forEach((movement) => {
      movement.points.forEach(point => {
        bounds.extend([point.lat, point.lon]);
        hasPoints = true;
      });
    });
    
    // Add order stops
    orderRoutes.forEach((route) => {
      route.stops.forEach(stop => {
        const coords = stop.location?.coordinates;
        if (coords) {
          bounds.extend([coords.lat, coords.lon]);
          hasPoints = true;
        }
      });
    });
    
    return hasPoints ? bounds : null;
  }, [filteredVehicleMovements, orderRoutes]);

  // Build color map - orders inherit colors from associated vehicles
  const colorMap = useMemo(() => {
    const vehicleToColor = new Map<string, string>();
    const orderToColor = new Map<string, string>();
    const orderToVehicles = new Map<string, string[]>();
    const vehicleToOrders = new Map<string, string[]>();
    
    // First, assign colors to vehicles from selectedItems
    selectedItems.forEach(item => {
      if (item.type === 'vehicle') {
        vehicleToColor.set(item.id, item.color);
      }
    });
    
    // Build association maps from order routes
    orderRoutes.forEach((route, orderId) => {
      if (route.associatedVehicles && route.associatedVehicles.length > 0) {
        orderToVehicles.set(orderId, route.associatedVehicles);
        
        route.associatedVehicles.forEach(vehicleId => {
          const existing = vehicleToOrders.get(vehicleId) || [];
          if (!existing.includes(orderId)) {
            vehicleToOrders.set(vehicleId, [...existing, orderId]);
          }
        });
      }
    });
    
    // Assign colors to orders - use associated vehicle color if available
    selectedItems.forEach(item => {
      if (item.type === 'order') {
        const associatedVehicles = orderToVehicles.get(item.id);
        if (associatedVehicles && associatedVehicles.length > 0) {
          // Use first associated vehicle's color if available
          const firstVehicle = associatedVehicles[0];
          if (vehicleToColor.has(firstVehicle)) {
            orderToColor.set(item.id, vehicleToColor.get(firstVehicle)!);
          } else {
            // Fallback to item's own color
            orderToColor.set(item.id, item.color);
          }
        } else {
          orderToColor.set(item.id, item.color);
        }
      }
    });
    
    return { vehicleToColor, orderToColor, orderToVehicles, vehicleToOrders };
  }, [selectedItems, orderRoutes]);

  // Get color for item (uses coordination)
  const getItemColor = (id: string, type: 'vehicle' | 'order'): string => {
    if (type === 'vehicle') {
      return colorMap.vehicleToColor.get(id) || STATUS_COLORS.info;
    }
    return colorMap.orderToColor.get(id) || STATUS_COLORS.info;
  };

  // Check if vehicle should be highlighted (associated with hovered order or hovered directly)
  const isVehicleHighlighted = (vehicleId: string): boolean => {
    // Highlighted if directly hovered
    if (hoveredVehicleId === vehicleId) return true;
    // Highlighted if associated order is hovered
    if (hoveredOrderId) {
      const associatedVehicles = colorMap.orderToVehicles.get(hoveredOrderId);
      return associatedVehicles?.includes(vehicleId) || false;
    }
    return false;
  };

  // Check if order should be highlighted (associated with hovered vehicle or hovered directly)
  const isOrderHighlighted = (orderId: string): boolean => {
    // Highlighted if directly hovered
    if (hoveredOrderId === orderId) return true;
    // Highlighted if associated vehicle is hovered
    if (hoveredVehicleId) {
      const associatedOrders = colorMap.vehicleToOrders.get(hoveredVehicleId);
      return associatedOrders?.includes(orderId) || false;
    }
    return false;
  };

  return (
    <div className="relative h-150 rounded-lg overflow-hidden">
      {loading && (
        <div className="absolute top-4 right-4 z-overlay bg-popover border border-border rounded-lg px-4 py-2 text-sm text-white shadow-lg">
          Loading map data...
        </div>
      )}
      
      <MapContainer
        center={[51.5074, -0.1278]} // London default
        zoom={10}
        style={{ height: '100%', width: '100%' }}
        zoomControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MapBoundsUpdater bounds={mapBounds} />
        
        {/* Vehicle GPS Trails */}
        {(() => {
          const trails = Array.from(filteredVehicleMovements.entries()).map(([vehicleId, movement]) => {
            const item = selectedItems.find(i => i.id === vehicleId && i.type === 'vehicle');
            if (!item) return null;
            
            const isHighlighted = isVehicleHighlighted(vehicleId);
            const vehicleColor = getItemColor(vehicleId, 'vehicle');
            const positions: [number, number][] = movement.points.map(p => [p.lat, p.lon]);
            
            return (
              <HoverablePolyline
                key={`vehicle-trail-${vehicleId}`}
                positions={positions}
                pathOptions={{
                  color: vehicleColor,
                  weight: isHighlighted ? 6 : 3,
                  opacity: isHighlighted ? 1 : 0.7,
                  className: isHighlighted ? 'vehicle-trail-highlighted' : '',
                }}
                onHover={() => setHoveredVehicleId(vehicleId)}
                onLeave={() => setHoveredVehicleId(null)}
              >
                <Popup>
                  <div className="text-sm">
                    <p className="font-semibold">{item.label}</p>
                    <p className="text-muted-foreground">Source: {item.source}</p>
                    <p className="text-muted-foreground">{movement.points.length} GPS points</p>
                  </div>
                </Popup>
              </HoverablePolyline>
            );
          });
          
          console.log('[InteractiveMap] Rendering vehicle trails:', trails.filter(t => t !== null).length);
          return trails;
        })()}
        
        {/* Vehicle Markers - rendered directly without clustering to avoid context issues */}
        {Array.from(filteredVehicleMovements.entries()).map(([vehicleId, movement]) => {
          const item = selectedItems.find(i => i.id === vehicleId && i.type === 'vehicle');
          if (!item || movement.points.length === 0) return null;
          
          // Show only the latest point as marker
          const latestPoint = movement.points[movement.points.length - 1];
          const vehicleColor = getItemColor(vehicleId, 'vehicle');
          const highlighted = isVehicleHighlighted(vehicleId);
          
          const customIcon = L.divIcon({
            html: `<div style="background-color: ${vehicleColor}; width: ${highlighted ? 18 : 12}px; height: ${highlighted ? 18 : 12}px; border-radius: 50%; border: ${highlighted ? 3 : 2}px solid white; box-shadow: 0 2px ${highlighted ? 8 : 4}px rgba(0,0,0,${highlighted ? 0.5 : 0.3}); transform: ${highlighted ? 'scale(1.3)' : 'scale(1)'}; transition: all 0.2s;"></div>`,
            className: 'custom-vehicle-marker',
            iconSize: L.point(highlighted ? 18 : 12, highlighted ? 18 : 12),
          });
          
          return (
            <HoverableMarker
              key={`vehicle-marker-${vehicleId}`}
              position={[latestPoint.lat, latestPoint.lon]}
              icon={customIcon}
              onHover={() => setHoveredVehicleId(vehicleId)}
              onLeave={() => setHoveredVehicleId(null)}
            >
              <Popup>
                <div className="text-sm">
                  <p className="font-semibold">{item.label}</p>
                  <p className="text-muted-foreground">Source: {item.source}</p>
                  <p className="text-muted-foreground">
                    {new Date(latestPoint.timestamp).toLocaleString()}
                  </p>
                  {latestPoint.speed !== undefined && (
                    <p className="text-muted-foreground">Speed: {latestPoint.speed.toFixed(1)} km/h</p>
                  )}
                </div>
              </Popup>
            </HoverableMarker>
          );
        })}
        
        {/* Order Routes */}
        {(() => {
          const routes = Array.from(orderRoutes.entries()).map(([orderId, route]) => {
            const item = selectedItems.find(i => i.id === orderId && i.type === 'order');
            if (!item) {
              console.log('[InteractiveMap] Order not in selectedItems:', orderId);
              return null;
            }
            
            // Get stops with coordinates
            const stopsWithCoords = route.stops.filter(s => s.location.coordinates);
            
            if (stopsWithCoords.length === 0) {
              console.log('[InteractiveMap] Order has no stops with coordinates:', orderId, route.stops.length);
              return null;
            }
            
            console.log('[InteractiveMap] Rendering order route:', orderId, stopsWithCoords.length, 'stops');
            
            // Draw polyline connecting stops
            const routePositions: [number, number][] = stopsWithCoords.map(s => [
              s.location.coordinates!.lat,
              s.location.coordinates!.lon,
            ]);
            
            const orderColor = getItemColor(orderId, 'order');
            const highlighted = isOrderHighlighted(orderId);

          return (
            <Fragment key={`order-route-${orderId}`}>
              {/* Route polyline */}
              <HoverablePolyline
                positions={routePositions}
                pathOptions={{
                  color: orderColor,
                  weight: highlighted ? 6 : 4,
                  opacity: highlighted ? 0.9 : 0.6,
                  dashArray: '10, 10',
                }}
                onHover={() => setHoveredOrderId(orderId)}
                onLeave={() => setHoveredOrderId(null)}
              >
                <Popup>
                  <div className="text-sm">
                    <p className="font-semibold">{item.label}</p>
                    <p className="text-muted-foreground">Source: {item.source}</p>
                    <p className="text-muted-foreground">{route.stops.length} stops</p>
                    {route.associatedVehicles.length > 0 && (
                      <p className="text-muted-foreground">Vehicles: {route.associatedVehicles.join(', ')}</p>
                    )}
                  </div>
                </Popup>
              </HoverablePolyline>
              
              {/* Stop markers */}
              {stopsWithCoords.map((stop, idx) => {
                const stopIcon = L.divIcon({
                  html: `<div style="background-color: white; width: ${highlighted ? 36 : 30}px; height: ${highlighted ? 36 : 30}px; border-radius: 50%; border: 3px solid ${orderColor}; display: flex; align-items: center; justify-content: center; color: ${orderColor}; font-weight: bold; font-size: ${highlighted ? 14 : 12}px; box-shadow: 0 2px ${highlighted ? 8 : 4}px rgba(0,0,0,${highlighted ? 0.5 : 0.3}); transition: all 0.2s;">${idx + 1}</div>`,
                  className: 'custom-stop-marker',
                  iconSize: L.point(highlighted ? 36 : 30, highlighted ? 36 : 30),
                  iconAnchor: L.point(highlighted ? 18 : 15, highlighted ? 18 : 15),
                });
                
                return (
                  <HoverableMarker
                    key={`order-stop-${orderId}-${idx}`}
                    position={[
                      stop.location!.coordinates!.lat,
                      stop.location!.coordinates!.lon,
                    ]}
                    icon={stopIcon}
                    onHover={() => setHoveredOrderId(orderId)}
                    onLeave={() => setHoveredOrderId(null)}
                  >
                    <Popup>
                      <div className="text-sm">
                        <p className="font-semibold">{stop.stopType}</p>
                        {stop.location.name && (
                          <p className="text-muted-foreground">{stop.location.name}</p>
                        )}
                        {stop.location.raw && (
                          <p className="text-muted-foreground text-xs">{stop.location.raw}</p>
                        )}
                        {stop.timing?.actual && (
                          <p className="text-muted-foreground text-xs mt-1">
                            {new Date(stop.timing.actual).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </Popup>
                  </HoverableMarker>
                );
              })}
            </Fragment>
          );
        });
        
        console.log('[InteractiveMap] Rendering order routes:', routes.filter(r => r !== null).length);
        return routes;
        })()}
      </MapContainer>
      
      {/* Add highlighting animation CSS */}
      <style>{`
        .vehicle-trail-highlighted {
          animation: pulse-trail 1.5s ease-in-out infinite;
        }
        
        @keyframes pulse-trail {
          0%, 100% {
            opacity: 0.7;
          }
          50% {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}
